import { Popover } from "@base-ui/react/popover";
import { ArrowUpDown, Check } from "lucide-react";
import styles from "./SortPopover.module.css";
import { useState } from "react";
import cx from "classnames";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

export type SortOption = "featured" | "a-z" | "z-a";

interface SortPopoverProps {
  option: SortOption;
  onChange: (value: SortOption) => void;
}

const sortLabels: Record<SortOption, string> = {
  featured: "Featured",
  "a-z": "A-Z",
  "z-a": "Z-A",
};

export function SortPopover({ option, onChange }: SortPopoverProps) {
  const [open, setOpen] = useState(false);

  const handleSelect = (option: SortOption) => {
    onChange(option);
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        className={styles.trigger}
        data-cy="sort-popover"
        data-value={option}
      >
        <ArrowUpDown
          className={styles.triggerIcon}
          strokeWidth={1.75}
          aria-hidden="true"
        />

        <span>{sortLabels[option]}</span>

        <span
          className={cx(styles.chevron, open ? styles.chevronOpen : "")}
          aria-hidden="true"
        >
          <ArrowDropDownIcon />
        </span>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Positioner sideOffset={8} align="end">
          <Popover.Popup className={styles.popup}>
            <div className={styles.header}>
              <Popover.Title className={styles.title}>
                Sort resources
              </Popover.Title>
            </div>

            <div
              className={styles.optionList}
              role="listbox"
              aria-label="Sort resources"
            >
              {Object.entries(sortLabels).map(([value, label]) => {
                const isSelected = value === option;

                return (
                  <button
                    key={value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={cx(
                      styles.option,
                      isSelected ? styles.optionSelected : "",
                    )}
                    onClick={() => {
                      handleSelect(value as SortOption);
                    }}
                    data-cy="sort-option"
                    data-value={value}
                  >
                    <span>{label}</span>

                    <span className={styles.optionIcon}>
                      {isSelected && (
                        <Check
                          fontSize="small"
                          strokeWidth={2.5}
                          aria-hidden="true"
                        />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

export default SortPopover;
